import { progressLabel, progressPercent } from '../../lib/progress';
import { projectColor } from '../../lib/project-color';
import type { ProgressCounts } from '../../lib/work-types';
import { cn } from '../../lib/utils';
import StatusPill from './StatusPill';

export function ProgressMeter({
  counts,
  label,
  className,
}: {
  counts: ProgressCounts;
  label?: string;
  className?: string;
}) {
  const percent = progressPercent(counts);
  const description = `${label ? `${label}: ` : ''}${counts.done} of ${counts.total} done`;
  return (
    <div className={cn('flex items-center gap-2', className)} title={description}>
      <div
        className="h-1.5 min-w-10 flex-1 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-label={description}
      >
        <div
          className={cn('h-full rounded-full', percent === 100 ? 'bg-emerald-500' : 'bg-primary')}
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="shrink-0 text-[11px] tabular-nums leading-4 text-muted-foreground">
        {label ? `${label} ` : ''}
        {progressLabel(counts)}
      </span>
    </div>
  );
}

const STATE_GROUP_PILL: Record<string, string> = {
  backlog: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  unstarted: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-200',
  started: 'bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-200',
  completed: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200',
  cancelled: 'bg-muted text-muted-foreground',
};

/** Tinted by state group so a stream's own state names keep a consistent colour story. */
export function StateGroupPill({ name, group, className }: { name: string; group: string; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium leading-4',
        STATE_GROUP_PILL[group] ?? STATE_GROUP_PILL.backlog,
        className,
      )}
    >
      {name}
    </span>
  );
}

/** The plain status everyone understands, with the stream's own step beside it. */
export function StatePill({
  name,
  group,
  blocked,
  className,
}: {
  name: string;
  group: string;
  blocked?: boolean;
  className?: string;
}) {
  return <StatusPill state={{ name, group }} blocked={blocked} className={className} />;
}

export function stateGroupDotClass(group: string): string {
  switch (group) {
    case 'unstarted':
      return 'bg-blue-500';
    case 'started':
      return 'bg-orange-500';
    case 'completed':
      return 'bg-emerald-500';
    case 'cancelled':
      return 'bg-muted-foreground/50';
    default:
      return 'bg-slate-500';
  }
}

const PRIORITY_PILL: Record<string, string> = {
  urgent: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
  high: 'bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-200',
  medium: 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  none: 'bg-muted text-muted-foreground',
};

export function priorityPillClass(priority: string): string {
  return PRIORITY_PILL[priority] ?? PRIORITY_PILL.none!;
}

export function priorityLabel(priority: string): string {
  if (priority === 'none') return 'No priority';
  return `${priority.charAt(0).toUpperCase()}${priority.slice(1)} priority`;
}

/** A stream's colour square with its initial, used wherever a stream is named. */
export function DepartmentBadge({
  projectId,
  name,
  size = 'sm',
}: {
  projectId: string;
  name: string;
  size?: 'sm' | 'md';
}) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded font-semibold text-white',
        size === 'md' ? 'h-6 w-6 text-xs' : 'h-4 w-4 text-[10px]',
      )}
      style={{ backgroundColor: projectColor(projectId) }}
    >
      {name.trim().charAt(0).toUpperCase() || '?'}
    </span>
  );
}
